import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Link,
  TextField,
  Divider,
  Collapse,
} from '@mui/material';
import {
  SportsSoccer as StravaIcon,
  ExpandMore,
  ExpandLess,
  Science
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { apiClient, setAuthToken } from '../../../utils/apiClient';
import { setToken, updateUser, clearAuth } from '../../../store/slices/authSlice';

interface UserResponse {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  profile_picture_url: string;
  role: 'athlete' | 'trainer' | 'both' | 'superuser';
  strava_athlete_id: number;
  strava_access_token: string;
  strava_refresh_token: string;
  strava_connected_at: string;
  strava_token_expires_at: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  last_login_at: string | null;
}

/**
 * LoginPage Component
 * 
 * Handles Strava OAuth authentication flow via backend.
 * Initiates OAuth and handles the callback with the final JWT token.
 */
const LoginPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | React.ReactNode | null>(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // Beta Login State
  const [showBetaLogin, setShowBetaLogin] = useState(false);
  const [betaEmail, setBetaEmail] = useState('');

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Handle OAuth callback from backend
  useEffect(() => {
    const token = searchParams.get('token');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      const errorMessages: Record<string, string> = {
        'invalid_state': 'Authentication failed due to invalid state. Please try again.',
        'strava_auth_failed': 'Failed to authenticate with Strava. Please try again.',
        'internal_error': 'An internal error occurred. Please try again later.',
      };
      setError(errorMessages[errorParam] || 'Authentication failed. Please try again.');
      setIsLoading(false);
      return;
    }

    if (token) {
      handleSuccessfulAuth(token);
    }
  }, [searchParams, dispatch]);

  /**
   * Handles successful authentication by storing token, fetching user data, and updating Redux
   */
  const handleSuccessfulAuth = async (token: string) => {
    setIsLoading(true);
    setError(null);

    try {
      // 1. Store the JWT token in the apiClient and localStorage
      setAuthToken(token);

      // 2. Fetch user profile from your backend
      const userData = await apiClient.get<UserResponse>('/users/me', { skipOfflineQueue: true });

      // 3. Update the Redux store with the user data and token
      dispatch(setToken(token));
      dispatch(updateUser({
        id: userData.id,
        email: userData.email,
        first_name: userData.first_name,
        last_name: userData.last_name,
        profile_picture_url: userData.profile_picture_url,
        role: userData.role,
        strava_athlete_id: userData.strava_athlete_id,
        is_active: userData.is_active,
        created_at: userData.created_at,
        updated_at: userData.updated_at,
        last_login_at: userData.last_login_at || new Date().toISOString(),
        strava_access_token: userData.strava_access_token,
        strava_refresh_token: userData.strava_refresh_token,
        strava_token_expires_at: userData.strava_token_expires_at,
      }));

      // 4. Navigate to the appropriate dashboard
      if (userData.role === 'athlete') {
        navigate('/athlete/dashboard');
      } else if (userData.role === 'trainer') {
        navigate('/trainer/athletes');
      } else if (userData.role === 'both') {
        navigate('/athlete/dashboard');
      } else if (userData.role === 'superuser') {
        navigate('/admin/dashboard');
      } else {
        navigate('/');
      }
    } catch (err) {
      console.error('Failed to complete authentication:', err);
      setError('Failed to retrieve user information. Please try logging in again.');
      dispatch(clearAuth());
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Initiates Strava OAuth flow through backend
   */
  const handleStravaConnect = async () => {
    if (isOffline) {
      setError('You are currently offline. Please connect to the internet to authenticate with Strava.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Call backend to get Strava authorization URL
      // We don't send a role for login, assuming backend handles existing users or defaults
      const response = await apiClient.get<{
        authorization_url: string;
      }>(`auth/strava/authorize`, {
        skipAuth: true,
        skipOfflineQueue: true,
      });

      // Redirect to Strava OAuth page
      window.location.href = response.authorization_url;
    } catch (err) {
      console.error('Failed to initiate Strava authentication:', err);
      setError('Failed to connect to Strava. Please try again.');
      setIsLoading(false);
    }
  };

  /**
   * Handles Beta Login Flow
   */
  const handleBetaLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isOffline) {
      setError('You are currently offline.');
      return;
    }

    if (!betaEmail) {
      setError('Please enter your email address.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Call backend to get Strava authorization URL with email param
      const response = await apiClient.get<{
        authorization_url: string;
      }>(`auth/strava/authorize?email=${encodeURIComponent(betaEmail)}`, {
        skipAuth: true,
        skipOfflineQueue: true,
      });

      // Redirect to Strava OAuth page (likely the custom app one)
      window.location.href = response.authorization_url;
    } catch (err: any) {
      console.error('Failed to initiate Beta authentication:', err);

      // Check for 404 User Not Found
      if (err.status === 404 || err.response?.status === 404 || err.message?.includes('404')) {
        setError(
          <span>
            User not found. To login via the beta program, you must first{' '}
            <Link
              component="button"
              variant="body2"
              color="inherit"
              onClick={() => navigate('/signup', { state: { openBeta: true } })}
              sx={{ fontWeight: 'bold', textDecoration: 'underline', verticalAlign: 'baseline' }}
            >
              register
            </Link>
            !
          </span>
        );
      } else {
        setError('Failed to connect to Strava. Please check your email and try again.');
      }
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Box sx={{ maxWidth: 500, width: '100%' }}>
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 2,
          }}
        >
          {/* Offline Banner */}
          {isOffline && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              You are currently offline. Authentication requires an internet connection.
            </Alert>
          )}

          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <StravaIcon sx={{ fontSize: 64, fill: '#FC4C02', mb: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
              Welcome Back
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Log in with your Strava account
            </Typography>
          </Box>

          {/* Connect Button */}
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleStravaConnect}
            disabled={isLoading || isOffline}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <StravaIcon />}
            sx={{
              bgcolor: '#FC4C02',
              color: 'white',
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              mb: 3,
              '&:hover': {
                bgcolor: '#E34402',
              },
              '&:disabled': {
                bgcolor: 'grey.300',
              },
            }}
          >
            {isLoading ? 'Connecting...' : 'Log In with Strava'}
          </Button>

          <Divider sx={{ my: 3 }}>
            <Typography variant="caption" color="text.secondary">
              OR
            </Typography>
          </Divider>

          {/* Beta Login Section */}
          <Box sx={{ mb: 3 }}>
            <Button
              fullWidth
              color="secondary"
              onClick={() => setShowBetaLogin(!showBetaLogin)}
              endIcon={showBetaLogin ? <ExpandLess /> : <ExpandMore />}
              startIcon={<Science />}
              sx={{ justifyContent: 'space-between', mb: 2 }}
            >
              Log In via Beta Program
            </Button>

            <Collapse in={showBetaLogin}>
              <Paper variant="outlined" sx={{ p: 3, bgcolor: 'background.paper' }}>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Enter the email you registered with for the Beta Program.
                </Typography>
                <form onSubmit={handleBetaLogin}>
                  <Stack spacing={2}>
                    <TextField
                      label="Email Address"
                      type="email"
                      fullWidth
                      required
                      size="small"
                      value={betaEmail}
                      onChange={(e) => setBetaEmail(e.target.value)}
                    />
                    <Button
                      type="submit"
                      variant="outlined"
                      color="secondary"
                      disabled={isLoading || isOffline}
                    >
                      Log In
                    </Button>
                  </Stack>
                </form>
              </Paper>
            </Collapse>
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Don't have an account?{' '}
              <Link
                component="button"
                variant="body2"
                onClick={() => navigate('/signup')}
                sx={{ fontWeight: 'bold', textDecoration: 'none' }}
              >
                Sign Up
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default LoginPage;